import * as React from 'react';
import {
  EnhancedAppointment,
  Country,
  BuildJsonType,
} from '@bb/domain/dist/types';
import classNames from 'classnames';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import errorReporter from '@app/errorReporter';
import fileStackClient from '@app/filestack';
import { Modal, Button } from 'antd';
import {
  PickerOptions,
  PickerResponse,
} from 'filestack-js/build/main/lib/picker';

import { useSendingReminder } from './hooks';
import Covid19AckTemplate from './COVID19AckTemplate';
import Covid19UploadedPdfModal from './Covid19UploadedPdfModal';

interface COVID19AptActionProps {
  appointment: BuildJsonType<EnhancedAppointment>;
  updateAppointment?: (ackType?: 'override' | 'upload') => void;
  showLinkOnly?: boolean;
  linkText?: string;
  isExternal?: boolean;
  isNonAdminListingAgent?: boolean;
  dashboardId?: string;
  retsId?: string;
  country?: Country;
}
type AppointmentSignSettings = {
  covid19Signed?: string;
  PDFAckFile?: string;
  template?: string;
  ip?: string;
};

const COVID19AptAction: React.FC<COVID19AptActionProps> = ({
  appointment,
  updateAppointment,
  showLinkOnly,
  linkText = 'Click here to view.',
  isExternal = false,
  isNonAdminListingAgent = false,
  dashboardId,
  retsId,
  country,
}) => {
  // modal states
  const [showSignedAck, setShowSignedAck] = React.useState(false);
  const [showUploadedPDF, setShowUploadedPDF] = React.useState(false);

  // UI data states
  const [validatedAck, setValidatedAck] = React.useState(
    !!appointment.covid19Signed,
  );
  const [covid19Overridden, setCovid19Overridden] = React.useState(
    !!appointment?.covid19Overridden,
  );
  const [PDFAckFile, setPDFAckFile] = React.useState('');
  const [appointmentSignSettings, setAppointmentSignSettings] =
    React.useState<AppointmentSignSettings>({});

  const { sendingReminder, covid19ReminderSent, sendReminder } =
    useSendingReminder({ appointment });

  React.useEffect(() => {
    const getAppointmentSignature = async () => {
      let url = `/covid19/appointment/${appointment.id}/settings`;
      if (isExternal) url = `/external${url}`;

      const { data } = await axios.get<AppointmentSignSettings>(url, {
        params: {
          dashboardId,
          retsId,
        },
      });
      if (data) {
        setAppointmentSignSettings(data);
        if (data.PDFAckFile) setPDFAckFile(data.PDFAckFile);
      }
    };
    if (appointment.covid19RequestedAck) getAppointmentSignature();
  }, [appointment.covid19RequestedAck]);

  const printAck = React.useRef();
  const handlePrint = useReactToPrint({
    content: () => printAck.current,
  });

  const openModal = (type: string) => {
    if (type === 'sign') setShowSignedAck(true);
    else if (type === 'pdf') setShowUploadedPDF(true);
  };
  const closeModal = (type: string) => {
    if (type === 'sign') setShowSignedAck(false);
    else if (type === 'pdf') setShowUploadedPDF(false);
  };

  const openFilePicker = async () => {
    const processResults = async (results: PickerResponse) => {
      const PDF = results.filesUploaded[0].url;
      await axios.post('/covid19/appointment/updateAction', {
        appointmentId: appointment.id,
        note: 'PDF ADDED',
        type: 'PDF',
        PDF,
      });
      setValidatedAck(true);
      setPDFAckFile(PDF);
      if (updateAppointment) updateAppointment('upload');
    };

    const opts: PickerOptions = {
      accept: 'application/pdf',
      maxFiles: 1,
      onUploadDone: results => processResults(results),
    };

    const picker = fileStackClient.picker(opts);
    picker.open();
  };

  const override = async () => {
    const { value } = await Swal.fire({
      title: 'Override and allow showing',
      text: 'Are you sure you want to allow this showing to proceed without an acknowledgment from the showing agent?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#62cb31',
      confirmButtonText: 'Confirm',
      reverseButtons: true,
    });
    if (value) {
      try {
        await axios.post('/covid19/appointment/updateAction', {
          appointmentId: appointment.id,
          note: 'COVID-19 FORM OVERRIDDEN',
          type: 'override',
        });
        setValidatedAck(true);
        setCovid19Overridden(true);
        if (updateAppointment) updateAppointment('override');
      } catch (err) {
        errorReporter.error(err);
      }
    }
  };

  return appointment.covid19RequestedAck ? (
    <>
      {!showLinkOnly && <h5>COVID-19</h5>}
      <div
        className={`appointment-small ${
          showLinkOnly ? '' : 'appointment-margin-top'
        }`}
      >
        <div>
          {PDFAckFile && (
            <Button
              style={{
                padding: '4px 0',
                margin: '0 5px',
                background: 'transparent',
                color: '#1665FC',
              }}
              type="link"
              onClick={() => openModal('pdf')}
            >
              {showLinkOnly ? linkText : 'View Uploaded Signed Acknowledgment'}
            </Button>
          )}
          {appointmentSignSettings.covid19Signed && (
            <Button
              style={{
                padding: '4px 0',
                margin: '0 5px',
                background: 'transparent',
                color: '#1665FC',
              }}
              type="link"
              onClick={() => openModal('sign')}
            >
              {showLinkOnly ? linkText : 'View Signed Acknowledgment'}{' '}
            </Button>
          )}
          {!validatedAck && !showLinkOnly && (
            <>
              <div>Acknowledgment form has not been signed.</div>

              {covid19ReminderSent ? (
                <div style={{ margin: '1em 0' }}>
                  A reminder email has been sent.
                </div>
              ) : (
                <button
                  style={{ margin: '1em 0' }}
                  className="btn btn-xs btn-info offset-y-2"
                  onClick={sendReminder}
                  disabled={sendingReminder}
                >
                  <i
                    className={classNames('fa', {
                      'fa-paper-plane': !sendingReminder,
                      'fa-spinner': sendingReminder,
                      'fa-spin': sendingReminder,
                    })}
                  />{' '}
                  Send Reminder
                </button>
              )}

              <div className="dropdown" style={{ padding: '5px' }}>
                <button
                  className="btn btn-primary dropdown-toggle"
                  type="button"
                  data-toggle="dropdown"
                >
                  Manage <span style={{ margin: '0px' }} className="caret" />
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <a onClick={openFilePicker}>+ Add PDF Acknowledgment</a>
                  </li>
                  {!isNonAdminListingAgent && (
                    <li>
                      <a onClick={override}>Override & Allow Showing</a>
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
          {covid19Overridden && (
            <div>The COVID-19 Acknowledgment form was overridden.</div>
          )}
        </div>
      </div>
      {/* view signed ack Modal */}
      <Modal
        data-cy="modal-popup"
        centered
        open={showSignedAck}
        onCancel={() => closeModal('sign')}
        footer={null}
        width={'auto'}
        bodyStyle={{ minHeight: '50vh', minWidth: '50vw' }}
        zIndex={10000}
      >
        <PrintModal ref={printAck}>
          <Covid19AckTemplate
            appointment={appointment}
            ackTemplate={appointmentSignSettings.template}
            country={country}
          />
          <SignHereDiv>
            <img
              style={{ width: '50%', maxWidth: '200px' }}
              src={appointmentSignSettings?.covid19Signed}
              alt="signature"
            />
          </SignHereDiv>
          <p>IP: {appointmentSignSettings?.ip}</p>
        </PrintModal>
        <button
          onClick={handlePrint}
          className="btn btn-success"
          style={{ marginRight: '3em' }}
        >
          Print PDF
        </button>
      </Modal>

      {/* view uploaded pdf modal */}
      <Covid19UploadedPdfModal
        onCancel={() => closeModal('pdf')}
        PDFAckFile={PDFAckFile}
        visible={showUploadedPDF}
      />
    </>
  ) : null;
};

export default COVID19AptAction;

const PrintModal = styled.div`
  @media print {
    padding: 0 40px;
  }
`;

const SignHereDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
  * {
    margin-right: 6px;
  }
`;
